/**
 * Galaxy painting ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_painting_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const galaxyPaintingIdeasPack: ToilePack = {
    slug: 'galaxy-painting-ideas',
    category: 'craft',
    name: 'Galaxy painting ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'swirl', label: 'Galaxy swirl', subject: 'a canvas of a galaxy swirl in purple, teal and pink with white star specks'},
        {key: 'forest', label: 'Forest galaxy', subject: 'a canvas of a galaxy sky seen above a black pine forest silhouette'},
        {key: 'planet', label: 'Rising planet', subject: 'a canvas of a ringed planet rising over a pink and blue nebula'},
    ],
    styles: [
        {key: 'sponge', label: 'Sponge blend', style: 'sponge-dabbed acrylic clouds blended soft with flicked white star splatter', palette: 'purple, teal and pink on a black ground with white stars', framing: 'The canvas on a wooden easel seen straight on, a plain pale wall behind'},
        {key: 'pour', label: 'Acrylic pour', style: 'acrylic pour with marbled cells of color under a glossy finish and dotted stars', palette: 'swirled purple, teal and magenta with white lacing', framing: 'The canvas leaning against a white wall on a light wooden floor'},
        {key: 'realism', label: 'Soft realism', style: 'smooth airbrushed-look blended acrylic with fine sharp stars and glowing dust', palette: 'deep indigo and violet glowing into pink and white', framing: 'The canvas lying flat on a wooden work table seen from directly above, brushes and a paint palette beside it'},
    ],
    directions: ['astronaut', 'black hole', 'aurora'],
    directionHint: 'try: astronaut',
    seo: {
        title: 'Galaxy Painting Ideas: Swirl, Forest Sky, Rising Planet',
        metaDescription: 'Nine galaxy painting ideas in purple, teal and pink: a swirl, a galaxy above pines and a planet over a nebula, in three techniques.',
        h1: 'Galaxy painting ideas: a swirling galaxy, a forest sky, a planet over a nebula',
        tagline: 'Nine galaxy canvases, three scenes, each made by sponge blending, acrylic pour or smooth blended acrylic.',
        intro: 'Galaxy canvases are blended clouds of purple, teal and pink flicked with white stars, a favorite first painting for teenagers. These nine show three scenes: a swirling galaxy in purple, teal and pink with white star specks, a galaxy sky seen above a black pine forest, and a ringed planet rising over a pink and blue nebula. Type another subject, such as an astronaut or a black hole, and the same technique carries it.',
        useCases: [
            "a teenager's first canvas",
            'a bedroom wall piece',
            'a gift for a space fan',
            'a desk piece under a lamp',
        ],
        faq: [
            {q: 'What is an acrylic pour?', a: 'Paint poured onto the canvas so the colors spread into marbled cells. The pour picture shows the result under a glossy finish.'},
            {q: 'Are these photographs of space?', a: 'No. All three are painted canvases made with paint, not telescope images.'},
            {q: 'What are the three techniques?', a: 'Sponge-dabbed blending with flicked white stars, acrylic pour with marbled cells, and smooth blended acrylic with fine stars.'},
            {q: 'Can I change the subject?', a: 'Yes. Type another subject and the galaxy colors and the three techniques carry it.'},
        ],
    },
}
